// React
import { memo, useEffect, useState } from "react";

// UI Components
import { Button } from "ui/components/Button/Button";
import { TextField } from "ui/components/TextField/TextField";
import { Loader } from "ui/components/Loader";
import { DropdownOption } from "ui/components/Dropdown/Dropdown/types";
import {
  DataWithPagination,
  initialPagination,
} from "ui/components/Table/consts";
import { useTranslate } from "helpers";
import {
  CALCULATION_VEHICLE_SERVICES_CREATE_PAGE_TITLE,
  CALCULATION_VEHICLE_SERVICES_EDIT_PAGE_TITLE,
} from "ui/components/PageTitle/consts";

// Services & API
import {
  createVehicleServiceAPI,
  getVehicleServiceAPI,
  updateVehicleServiceAPI,
} from "services/api/calculations/vehicleservice/api";
import { initialVehicleServicesEditForm } from "services/api/calculations/vehicleservice/consts";
import { validateYup } from "services/forms/validation/validation";
import { Errors } from "services/forms/validation/types";
import {
  useHandleDropdownChange,
  useHandleTextFieldChange,
} from "services/forms/hooks";
import { initialDataWithPagination } from "services/api/consts";
import { getVehicleGroupsAPI } from "services/api/calculations/vehiclegroup/api";

// Types
import { VehicleServicesEditProps } from "./types";
import { VehicleServicesEditFormValues } from "services/api/calculations/vehicleservice/types";
import { VehicleGroupRow } from "services/api/calculations/vehiclegroup/types";

// Validation
import { CALCULATION_VEHICLE_SERVICES_EDIT_FORM_SCHEMA } from "./validation";

const VehicleServicesEditCard: React.FC<VehicleServicesEditProps> = (props) => {
  const { t } = useTranslate();
  const { vehicleServicesId, onSuccess } = props;
  const [errors, setErrors] = useState<Errors>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [vehicleGroups, setVehicleGroups] = useState<
    DataWithPagination<VehicleGroupRow>
  >(initialDataWithPagination);

  const [formValues, setFormValues] = useState<VehicleServicesEditFormValues>(
    initialVehicleServicesEditForm
  );

  const title = vehicleServicesId
    ? CALCULATION_VEHICLE_SERVICES_EDIT_PAGE_TITLE.text
    : CALCULATION_VEHICLE_SERVICES_CREATE_PAGE_TITLE.text;

  useEffect(() => {
    if (vehicleServicesId) {
      fetchVehicleServices(vehicleServicesId);
      fetchVehicleGroups();
    }
  }, [vehicleServicesId]);

  const fetchVehicleServices = async (id: string) => {
    if (id === undefined) {
      setFormValues(initialVehicleServicesEditForm);
      return;
    }

    setIsLoading(true);

    let result;
    try {
      result = await getVehicleServiceAPI(id);
    } catch {
      setIsLoading(false);
      return;
    }

    setFormValues(result);

    setIsLoading(false);

    setErrors({});
  };

  const fetchVehicleGroups = async () => {
    setIsLoading(true);

    let result;

    try {
      result = await getVehicleGroupsAPI(initialPagination);
    } catch {
      setIsLoading(false);
      return;
    }

    setVehicleGroups(result);
    setIsLoading(false);
  };

  const handleFormSubmit = async (e: any) => {
    e.preventDefault();

    const isValid = validateYup(
      formValues,
      CALCULATION_VEHICLE_SERVICES_EDIT_FORM_SCHEMA,
      setErrors
    );

    if (!isValid) {
      return;
    }

    setIsLoading(true);

    try {
      let result;
      if (!!vehicleServicesId) {
        result = await updateVehicleServiceAPI(vehicleServicesId, formValues);
      } else {
        result = await createVehicleServiceAPI(formValues);
      }
      if (result && onSuccess) onSuccess();
    } catch (e) {
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
  };

  const handleTextFieldChange = useHandleTextFieldChange(
    setFormValues,
    formValues
  );

  const handleDropdownChange = useHandleDropdownChange(
    setFormValues,
    formValues
  );

  const vehicleGroupItems: DropdownOption[] = vehicleGroups.data.map((i) => {
    return {
      value: i.id.toString(),
      text: i.name,
    };
  });

  return (
    <>
      <div className="flex gap-4">
        <div className="w-full m-auto">
          <div className="space-y-3">
            <form noValidate className="text-left">
              <div className="px-5 pb-3 mb-2">
                <div className="flex flex-row mb-3">
                  <div className="flex-1 ">
                    <TextField
                      type="text"
                      name="name"
                      label={t("admin.calculation.vehicle.services.edit.name")}
                      placeholder={t(
                        "admin.calculation.vehicle.services.edit.name.placeholder"
                      )}
                      onChange={handleTextFieldChange}
                      error={errors.name}
                      value={formValues.name}
                      required
                      endIcon=""
                    ></TextField>
                  </div>
                </div>
                <div className="flex flex-row mb-3">
                  <div className="flex-1 ">
                    <TextField
                      type="number"
                      name="ratePerKm"
                      label={t(
                        "admin.calculation.vehicle.services.edit.rateperkm"
                      )}
                      placeholder={t(
                        "admin.calculation.vehicle.services.edit.rateperkm.placeholder"
                      )}
                      onChange={handleTextFieldChange}
                      error={errors.ratePerKm}
                      value={formValues.ratePerKm}
                      required
                      endIcon=""
                      groupAppend="%"
                    ></TextField>
                  </div>
                </div>
              </div>
              <div className="px-5 pb-5 flex justify-end ">
                <Button
                  variant="primary"
                  size="full"
                  children={t("admin.save")}
                  onClick={handleFormSubmit}
                />
              </div>
            </form>
          </div>
        </div>
      </div>

      <Loader isLoading={isLoading ?? false}> </Loader>
    </>
  );
};
export default memo(VehicleServicesEditCard);
